
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BikeRevenueData } from './types';
import PaybackProgressBar from './PaybackProgressBar';
import PaybackStatusBadge from './PaybackStatusBadge';

interface BikeRevenueTableProps {
  data: BikeRevenueData[];
  loading: boolean;
  dateRangeText: string;
  targetRevenue?: number;
}

const BikeRevenueTable: React.FC<BikeRevenueTableProps> = ({ 
  data, 
  loading, 
  dateRangeText,
  targetRevenue = 55000
}) => {
  // Функция для определения цвета строки и текста на основе окупаемости
  const getRowStyles = (revenue: number) => {
    const percentage = (revenue / targetRevenue) * 100;
    if (percentage < 20) {
      return {
        bgClass: 'bg-red-100 hover:bg-red-150 dark:bg-red-900/30 dark:hover:bg-red-900/40',
        textClass: 'text-red-900 dark:text-red-100'
      };
    }
    if (percentage < 50) {
      return {
        bgClass: 'bg-orange-100 hover:bg-orange-150 dark:bg-orange-900/30 dark:hover:bg-orange-900/40',
        textClass: 'text-orange-900 dark:text-orange-100'
      };
    }
    if (percentage < 80) {
      return {
        bgClass: 'bg-yellow-100 hover:bg-yellow-150 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/40',
        textClass: 'text-yellow-900 dark:text-yellow-100'
      };
    }
    if (percentage < 100) {
      return {
        bgClass: 'bg-green-100 hover:bg-green-150 dark:bg-green-900/30 dark:hover:bg-green-900/40',
        textClass: 'text-green-900 dark:text-green-100'
      };
    }
    return {
      bgClass: 'bg-emerald-100 hover:bg-emerald-150 dark:bg-emerald-900/30 dark:hover:bg-emerald-900/40',
      textClass: 'text-emerald-900 dark:text-emerald-100'
    };
  };

  // Функция для расчета процента окупаемости
  const getPaybackPercentage = (revenue: number) => {
    return ((revenue / targetRevenue) * 100).toFixed(1);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Детальная информация по выручке за {dateRangeText}</CardTitle>
        <p className="text-sm text-muted-foreground">
          Цель: окупаемость {targetRevenue.toLocaleString()} ₽ за весь период эксплуатации
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center items-center h-20">
            <p>Загрузка данных...</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-semibold">Серийный номер</TableHead>
                  <TableHead className="font-semibold">Модель</TableHead>
                  <TableHead className="text-right font-semibold">Выручка (₽)</TableHead>
                  <TableHead className="text-center font-semibold">Окупаемость</TableHead>
                  <TableHead className="text-right font-semibold">% окупаемости</TableHead>
                  <TableHead className="text-right font-semibold">Кол-во аренд</TableHead>
                  <TableHead className="text-right font-semibold">Средняя выручка (₽)</TableHead>
                  <TableHead className="text-center font-semibold">Статус</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.length > 0 ? (
                  data.map((bike) => {
                    const styles = getRowStyles(bike.totalRevenue);
                    return (
                      <TableRow 
                        key={bike.bikeId}
                        className={`transition-colors border-b-2 ${styles.bgClass}`}
                      >
                        <TableCell className={`font-semibold ${styles.textClass}`}>
                          {bike.serialNumber}
                        </TableCell>
                        <TableCell className={`font-medium ${styles.textClass}`}>
                          {bike.model}
                        </TableCell>
                        <TableCell className={`text-right font-bold text-lg ${styles.textClass}`}>
                          {bike.totalRevenue.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="w-24">
                            <PaybackProgressBar 
                              revenue={bike.totalRevenue} 
                              targetRevenue={targetRevenue}
                            />
                          </div>
                        </TableCell>
                        <TableCell className={`text-right font-bold text-lg ${styles.textClass}`}>
                          {getPaybackPercentage(bike.totalRevenue)}%
                        </TableCell>
                        <TableCell className={`text-right font-medium ${styles.textClass}`}>
                          {bike.rentalCount}
                        </TableCell>
                        <TableCell className={`text-right font-medium ${styles.textClass}`}>
                          {bike.averageRevenue.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <PaybackStatusBadge 
                            revenue={bike.totalRevenue}
                            targetRevenue={targetRevenue}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-4">
                      Нет данных для отображения
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default BikeRevenueTable;
